import { useReducer } from "react";

import {
  IStoreDeposit,
  IUpdateDepositNsuResult,
} from "src/@types/store-deposit-maintenance";

export type DepositModalState =
  | { readonly kind: "none" }
  | {
      readonly kind: "nsu";
      readonly deposit: IStoreDeposit;
      readonly confirmation: IUpdateDepositNsuResult | null;
    }
  | { readonly kind: "account"; readonly deposit: IStoreDeposit }
  | { readonly kind: "details"; readonly deposit: IStoreDeposit };

export type DepositModalAction =
  | { type: "OPEN_NSU"; payload: IStoreDeposit }
  | { type: "REQUIRE_NSU_CONFIRMATION"; payload: IUpdateDepositNsuResult }
  | { type: "OPEN_ACCOUNT"; payload: IStoreDeposit }
  | { type: "OPEN_DETAILS"; payload: IStoreDeposit }
  | { type: "CLOSE" };

export const CLOSED_DEPOSIT_MODAL: DepositModalState = { kind: "none" };

export function depositModalReducer(
  state: DepositModalState,
  action: DepositModalAction,
): DepositModalState {
  if (action.type === "OPEN_NSU") {
    return { kind: "nsu", deposit: action.payload, confirmation: null };
  }

  if (action.type === "REQUIRE_NSU_CONFIRMATION") {
    if (state.kind !== "nsu") return state;
    return { ...state, confirmation: action.payload };
  }

  if (action.type === "OPEN_ACCOUNT") {
    return { kind: "account", deposit: action.payload };
  }

  if (action.type === "OPEN_DETAILS") {
    return { kind: "details", deposit: action.payload };
  }

  if (action.type === "CLOSE") {
    return CLOSED_DEPOSIT_MODAL;
  }

  return state;
}

export interface DepositModalMethods {
  readonly openNsu: (deposit: IStoreDeposit) => void;
  readonly requireNsuConfirmation: (result: IUpdateDepositNsuResult) => void;
  readonly openAccount: (deposit: IStoreDeposit) => void;
  readonly openDetails: (deposit: IStoreDeposit) => void;
  readonly close: () => void;
}

export interface UseDepositModalReducer {
  readonly state: DepositModalState;
  readonly methods: DepositModalMethods;
}

export function useDepositModalReducer(): UseDepositModalReducer {
  const [state, dispatch] = useReducer(
    depositModalReducer,
    CLOSED_DEPOSIT_MODAL,
  );

  const methods: DepositModalMethods = {
    openNsu: (deposit: IStoreDeposit): void =>
      dispatch({ type: "OPEN_NSU", payload: deposit }),

    requireNsuConfirmation: (result: IUpdateDepositNsuResult): void =>
      dispatch({ type: "REQUIRE_NSU_CONFIRMATION", payload: result }),

    openAccount: (deposit: IStoreDeposit): void =>
      dispatch({ type: "OPEN_ACCOUNT", payload: deposit }),

    openDetails: (deposit: IStoreDeposit): void =>
      dispatch({ type: "OPEN_DETAILS", payload: deposit }),

    close: (): void => dispatch({ type: "CLOSE" }),
  };

  return { state, methods };
}
